import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PEOPLE, spaceOf } from "@/lib/spaces";
import { playSound } from "@/lib/fun";

const SPIN_MS = 4200;

const arc = (i, n, r = 100) => {
  const a0 = ((i / n) * 2 - 0.5) * Math.PI;
  const a1 = (((i + 1) / n) * 2 - 0.5) * Math.PI;
  const p = (a) => `${100 + r * Math.cos(a)} ${100 + r * Math.sin(a)}`;
  return `M100 100 L${p(a0)} A${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${p(a1)} Z`;
};

const Wheel = ({ rot, winner, onClose }) => {
  const n = PEOPLE.length;
  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm" onClick={winner ? onClose : undefined} data-testid="random-wheel-overlay">
      <p className="mb-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">Random Wheel</p>
      <div className="relative h-[min(70vw,60vh)] w-[min(70vw,60vh)]">
        <span className="absolute left-1/2 top-[-14px] z-10 h-0 w-0 -translate-x-1/2 border-x-[16px] border-t-[28px] border-x-transparent border-t-white drop-shadow" />
        <svg viewBox="0 0 200 200" className="h-full w-full drop-shadow-2xl" style={{ transform: `rotate(${rot}deg)`, transition: `transform ${SPIN_MS}ms cubic-bezier(0.15, 0.85, 0.2, 1)` }} data-testid="random-wheel-svg">
          {PEOPLE.map((p, i) => {
            const mid = ((i + 0.5) / n) * 360;
            return (
              <g key={p.key}>
                <path d={arc(i, n)} fill={p.color} stroke="#0b1020" strokeWidth="1.5" />
                <text x="100" y="38" transform={`rotate(${mid} 100 100)`} textAnchor="middle" className="fill-black font-display text-[13px] font-extrabold">{p.emoji ? `${p.emoji} ` : ""}{p.name}</text>
              </g>
            );
          })}
          <circle cx="100" cy="100" r="14" fill="#0b1020" stroke="white" strokeWidth="2" />
        </svg>
      </div>
      <p className="mt-8 h-12 font-display text-4xl font-extrabold" style={{ color: winner ? spaceOf(winner.username).color : "transparent" }} data-testid="random-wheel-winner">{winner ? `${winner.name}!` : "…"}</p>
      {winner && <p className="mt-2 text-sm text-white/60">Tap anywhere to close</p>}
    </div>,
    document.body,
  );
};

export const useRandomWheel = (onDone) => {
  const [open, setOpen] = useState(false);
  const [rot, setRot] = useState(0);
  const [winner, setWinner] = useState(null);
  const busy = useRef(false);
  const timer = useRef(null);
  const close = () => { clearTimeout(timer.current); setOpen(false); setWinner(null); };
  const spin = async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.post("/fun/tea-round");
      const n = PEOPLE.length;
      const i = Math.max(0, PEOPLE.findIndex((p) => p.key === data.username));
      const center = ((i + 0.5) / n) * 360;
      setWinner(null);
      setOpen(true);
      setTimeout(() => setRot((r) => r + 360 * 6 + ((((-center - r) % 360) + 360) % 360)), 80);
      setTimeout(() => {
        setWinner(data);
        playSound("arcade");
        onDone?.(data);
        busy.current = false;
        timer.current = setTimeout(close, 8000);
      }, SPIN_MS + 180);
    } catch (e) {
      busy.current = false;
      toast.error(errMsg(e));
    }
  };
  const node = open ? <Wheel rot={rot} winner={winner} onClose={close} /> : null;
  return [spin, node];
};
